/**
 * TITAM AI - DataTable Component
 * Tabela com ordenação dinâmica, seguindo design minimalista Apple
 */

import { useState, useMemo } from 'react'
import Icon from './icons.jsx'
import { Skeleton } from './UX.jsx'

/**
 * HeaderCell - Célula de cabeçalho com ordenação
 */
function HeaderCell({ column, sortConfig, onSort }) {
  const isSorted = sortConfig?.key === column.key
  const direction = sortConfig?.direction

  return (
    <th
      className={`dt-header-cell ${column.align || 'left'} ${isSorted ? 'sorted' : ''} ${column.sortable !== false ? 'sortable' : ''}`}
      style={{ width: column.width }}
      onClick={() => column.sortable !== false && onSort(column.key)}
    >
      <div className="dt-header-content">
        <span className="dt-header-label">{column.label}</span>
        {column.sortable !== false && (
          <span className={`dt-sort-icon ${isSorted ? 'active' : ''}`}>
            {isSorted ? (
              <Icon n={direction === 'asc' ? 'chevron_u' : 'chevron_d'} s={14} />
            ) : (
              <span className="dt-sort-placeholder">
                <Icon n="chevron_d" s={14} />
              </span>
            )}
          </span>
        )}
      </div>
    </th>
  )
}

/**
 * DataCell - Célula de dados com formatação
 */
function DataCell({ column, row, value }) {
  const formatted = useMemo(() => {
    if (column.format === 'currency') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(value || 0)
    }
    if (column.format === 'number') {
      return new Intl.NumberFormat('pt-BR').format(value || 0)
    }
    if (column.format === 'percent') {
      return `${(value || 0).toFixed(2)}%`
    }
    if (column.format === 'multiplier') {
      return `${(value || 0).toFixed(1)}x`
    }
    if (column.format === 'decimal') {
      return (value || 0).toFixed(2)
    }
    return value
  }, [column.format, value])

  const cellClass = `dt-cell ${column.align || 'left'} ${column.key}`

  // Renderização customizada
  if (column.render) {
    return <td className={cellClass}>{column.render(value, row)}</td>
  }

  // Tipo de badge
  if (column.type === 'badge') {
    const badgeClass = value === 'active' || value === 'Ativo' ? 'badge-green' :
                      value === 'paused' || value === 'Pausado' ? 'badge-amber' :
                      value === 'error' || value === 'Erro' ? 'badge-red' :
                      'badge-neutral'
    const dotClass = value === 'active' || value === 'Ativo' ? 'dot-green' :
                     value === 'paused' || value === 'Pausado' ? 'dot-amber' :
                     value === 'error' || value === 'Erro' ? 'dot-red' :
                     'dot-gray'
    return (
      <td className={cellClass}>
        <span className={`badge ${badgeClass}`}>
          <span className={`dot ${dotClass}`} />
          {value}
        </span>
      </td>
    )
  }

  // Tipo de progresso
  if (column.type === 'progress') {
    const pct = Math.min(100, Math.max(0, value || 0))
    return (
      <td className={cellClass}>
        <div className="dt-progress-wrapper">
          <span className="dt-progress-value">{pct.toFixed(1)}%</span>
          <div className="dt-progress-bar">
            <div className="dt-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </td>
    )
  }

  // Tipo de trend (crescente/decrescente)
  if (column.type === 'trend') {
    const isPositive = value > 0
    return (
      <td className={`${cellClass} ${isPositive ? 'positive' : 'negative'}`}>
        <span className="dt-trend">
          <Icon n={isPositive ? 'trend_up' : 'trend_down'} s={12} />
          {Math.abs(value || 0).toFixed(1)}%
        </span>
      </td>
    )
  }

  return <td className={cellClass}>{formatted}</td>
}

/**
 * DataTable - Componente principal de tabela
 * @param {Array} columns - Configuração das colunas
 * @param {Array} data - Dados da tabela
 * @param {boolean} loading - Estado de carregamento
 * @param {string} emptyText - Texto quando não há dados
 * @param {Function} onRowClick - Callback ao clicar na linha
 * @param {string} keyExtractor - Chave única para cada linha
 * @param {boolean} striped - Linhas alternadas
 * @param {boolean} hoverable - Efeito hover nas linhas
 */
export function DataTable({
  columns,
  data = [],
  loading = false,
  emptyText = 'Nenhum dado encontrado',
  onRowClick,
  keyExtractor = 'id',
  striped = true,
  hoverable = true,
  pagination = false,
  pageSize = 10,
  scrollable = false,
  maxHeight,
}) {
  const containerClass = `dt-container ${scrollable ? 'dt-container--scroll' : ''}`
  const containerStyle = scrollable && maxHeight ? { maxHeight } : undefined
  const [sortConfig, setSortConfig] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)

  // Ordenação
  const sortedData = useMemo(() => {
    if (!sortConfig) return data

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key]
      const bVal = b[sortConfig.key]

      // Tratamento para valores nulos
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortConfig.direction === 'asc' ? -1 : 1
      if (bVal == null) return sortConfig.direction === 'asc' ? 1 : -1

      // Comparação numérica
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
      }

      // Comparação de strings
      const aStr = String(aVal).toLowerCase()
      const bStr = String(bVal).toLowerCase()
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr, 'pt-BR')
      }
      return bStr.localeCompare(aStr, 'pt-BR')
    })
  }, [data, sortConfig])

  // Paginação
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, pagination, currentPage, pageSize])

  const totalPages = Math.ceil(sortedData.length / pageSize)

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' }
        }
        return null // Remove ordenação
      }
      return { key, direction: 'asc' }
    })
  }

  // Skeleton loading
  if (loading) {
    return (
      <div className="dt-container">
        <div className="dt-skeleton">
          <Skeleton width="100%" height={40} style={{ marginBottom: 1 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={48} style={{ marginBottom: 1 }} />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="dt-container">
        <div className="dt-empty">
          <Icon n="chart" s={32} c="var(--border)" />
          <span>{emptyText}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="dt-wrapper">
      <div className={containerClass} style={containerStyle}>
        <table className="dt-table">
          <thead>
            <tr>
              {columns.map(col => (
                <HeaderCell
                  key={col.key}
                  column={col}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, idx) => (
              <tr
                key={row[keyExtractor] || idx}
                className={`${striped && idx % 2 === 1 ? 'striped' : ''} ${hoverable ? 'hoverable' : ''} ${onRowClick ? 'clickable' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map(col => (
                  <DataCell
                    key={col.key}
                    column={col}
                    row={row}
                    value={row[col.key]}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="dt-pagination">
          <button
            className="dt-page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            <Icon n="chevron_l" s={14} />
          </button>
          <span className="dt-page-info">
            {currentPage} de {totalPages}
          </span>
          <button
            className="dt-page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            <Icon n="chevron_r" s={14} />
          </button>
        </div>
      )}
    </div>
  )
}


